import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Banner, bannerService } from "@/services/bannerService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeft,
  Trash2,
  Calendar,
  CheckCircle2,
  XCircle,
  Eye,
  Save,
  Megaphone,
  X,
  Building2,
  Monitor,
  Newspaper,
  Maximize2
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import axios from "@/lib/axios";
import { RichTextEditor } from "@/components/email/RichTextEditor";

interface Company {
  _id: string;
  name: string;
}

// Önizleme için değişkenleri örnek değerlerle değiştir
const replacePreviewVariables = (content: string) => {
  if (!content) return content;
  return content
    .replace(/\{\{username\}\}/gi, 'Örnek Kullanıcı')
    .replace(/\{\{firma\}\}/gi, 'Örnek Firma')
    .replace(/\{\{email\}\}/gi, 'ornek@email.com')
    .replace(/\{\{tarih\}\}/gi, new Date().toLocaleDateString('tr-TR'));
};

export default function BannerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNewBanner = id === "new";

  // Lokal tarih/saat için yardımcı fonksiyon
  const getLocalDateISOString = (date: Date) => {
    return date.toISOString();
  };

  const now = new Date();
  const oneWeekLater = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

  // Form state
  const [formData, setFormData] = useState<Partial<Banner>>({
    title: "",
    content: "<p>Merhaba {{username}},</p>",
    displayType: "banner",
    backgroundColor: "#FFE600",
    textColor: "#000000",
    buttonText: "",
    buttonLink: "",
    iconSvg: "",
    startDate: getLocalDateISOString(now),
    endDate: getLocalDateISOString(oneWeekLater),
    targetCompanies: [],
    active: true
  });

  const [isSaving, setIsSaving] = useState(false);

  // Fetch banner data
  const { data: banner, isLoading } = useQuery<Banner, Error>({
    queryKey: ["banner", id],
    queryFn: async () => {
      if (isNewBanner) return formData as Banner;
      const data = await bannerService.getBannerById(id!);
      return data;
    },
    enabled: !!id && !isNewBanner,
  });

  // Fetch companies for targeting
  const { data: companies = [] } = useQuery<Company[], Error>({
    queryKey: ["companies"],
    queryFn: async () => {
      const { data } = await axios.get('/companies');
      return data;
    },
  });

  // Set form data when banner data is loaded
  useEffect(() => {
    if (banner && !isNewBanner) {
      setFormData(banner);
    }
  }, [banner, isNewBanner]);

  // Handle form changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle content change from RichTextEditor
  const handleContentChange = (html: string) => {
    setFormData(prev => ({ ...prev, content: html }));
  };

  // Handle date changes
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (!value) return;

    try {
      // datetime-local input yerel saat verir, direkt Date objesi oluştur
      const localDate = new Date(value);
      if (isNaN(localDate.getTime())) return;
      // toISOString otomatik olarak UTC'ye çevirir
      const isoString = localDate.toISOString();
      setFormData(prev => ({ ...prev, [name]: isoString }));
    } catch (error) {
      console.error('Date conversion error:', error);
    }
  };

  // Handle color changes
  const handleColorChange = (color: string, field: "backgroundColor" | "textColor") => {
    setFormData(prev => ({ ...prev, [field]: color }));
  };

  // Handle company selection
  const handleCompanyToggle = (companyId: string) => {
    setFormData(prev => {
      const currentTargets = prev.targetCompanies || [];
      const exists = currentTargets.includes(companyId);
      if (exists) {
        return { ...prev, targetCompanies: currentTargets.filter(id => id !== companyId) };
      } else {
        return { ...prev, targetCompanies: [...currentTargets, companyId] };
      }
    });
  };

  // Save banner mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isNewBanner) {
        return await bannerService.createBanner(formData as Omit<Banner, '_id'>);
      } else {
        return await bannerService.updateBanner(id!, formData);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      toast.success(isNewBanner ? "Banner başarıyla oluşturuldu." : "Banner başarıyla güncellendi.");
      if (isNewBanner) {
        navigate("/notices");
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || "İşlem sırasında bir hata oluştu.";
      toast.error(message);
    }
  });

  // Delete banner mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      return await bannerService.deleteBanner(id!);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      toast.success("Banner başarıyla silindi.");
      navigate("/notices");
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || "Silme işlemi sırasında bir hata oluştu.";
      toast.error(message);
    }
  });

  // Toggle banner active state
  const toggleActiveMutation = useMutation({
    mutationFn: async () => {
      if (formData.active) {
        return await bannerService.deactivateBanner(id!);
      } else {
        return await bannerService.activateBanner(id!);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banner', id] });
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      setFormData(prev => ({ ...prev, active: !prev.active }));
      toast.success(formData.active ? "Banner devre dışı bırakıldı." : "Banner aktifleştirildi.");
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || "İşlem sırasında bir hata oluştu.";
      toast.error(message);
    }
  });

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    saveMutation.mutate(undefined, {
      onSettled: () => setIsSaving(false)
    });
  };

  // Format date for input fields - yerel saat olarak göster
  const formatDateForInput = (dateString: string | undefined) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      return '';
    }
  };

  return (
    <div className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Hero Section */}
        <div className="rounded-2xl overflow-hidden shadow-lg">
          <div
            className="flex"
            style={{
              background: 'linear-gradient(to right, #6b21a8, #a855f7)',
            }}
          >
            <div className="p-8 flex justify-between items-start relative w-full">
              <div className="space-y-3 flex-1">
                <button
                  onClick={() => navigate("/notices")}
                  className="inline-flex items-center gap-2 text-sm font-medium bg-transparent text-white/80 hover:text-white hover:bg-white/10 rounded-md px-3 py-2 -ml-2 transition-colors border-none outline-none"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Geri
                </button>
                <h1 className="text-3xl font-bold text-white">
                  {isNewBanner ? "Yeni Banner Oluştur" : "Banner Düzenle"}
                </h1>
                <p className="text-white/90 text-sm max-w-xl leading-relaxed">
                  {isNewBanner
                    ? "Kullanıcılara gösterilecek yeni bir banner oluşturun"
                    : "Banner içeriğini ve ayarlarını düzenleyin"}
                </p>
              </div>
              <div
                className="w-40 h-40 rounded-lg overflow-hidden relative z-10 flex items-center justify-center ml-8"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }}
              >
                <Megaphone className="w-20 h-20 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>

        {/* Durum ve Aksiyonlar */}
        {!isNewBanner && (
          <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-600">Banner Durumu:</span>
              <Badge
                className={
                  formData.active
                  ? "bg-green-100 text-green-800 border-green-300"
                  : "bg-gray-100 text-gray-800 border-gray-300"
                }
              >
                {formData.active ? "Aktif" : "Pasif"}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggleActiveMutation.mutate()}
                disabled={isSaving || toggleActiveMutation.isPending}
              >
                {formData.active
                  ? <><XCircle className="h-4 w-4 mr-2" /> Devre Dışı Bırak</>
                  : <><CheckCircle2 className="h-4 w-4 mr-2" /> Aktifleştir</>
                }
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Sil
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Banner'ı silmek istediğinize emin misiniz?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Bu işlem geri alınamaz. Banner tüm kullanıcılardan kaldırılacaktır.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>İptal</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-red-600 hover:bg-red-700"
                      onClick={() => deleteMutation.mutate()}
                    >
                      Sil
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}

        {isLoading && !isNewBanner ? (
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Sol Panel - Form Alanları */}
              <div className="lg:col-span-2 space-y-6">
                {/* Banner Bilgileri */}
                <Card className="overflow-hidden border border-slate-200 shadow-sm bg-white rounded-xl">
                  <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                    <CardTitle className="text-base font-semibold text-slate-800">Banner Bilgileri</CardTitle>
                    <CardDescription>Banner başlığı ve içeriğini düzenleyin</CardDescription>
                  </CardHeader>
                  <CardContent className="p-6 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Başlık</Label>
                        <Input
                          name="title"
                          value={formData.title || ''}
                          onChange={handleChange}
                          placeholder="Banner başlığı"
                          className="h-11 border-slate-200"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Görüntüleme Tipi</Label>
                        <Select
                          value={formData.displayType || 'banner'}
                          onValueChange={(value: 'banner' | 'news' | 'modal') =>
                            setFormData(prev => ({ ...prev, displayType: value }))
                          }
                        >
                          <SelectTrigger className="h-11 border-slate-200">
                            <SelectValue placeholder="Tip seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="banner">
                              <div className="flex items-center gap-2">
                                <Monitor className="h-4 w-4 text-blue-500" />
                                <span>Banner (Header üstü)</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="news">
                              <div className="flex items-center gap-2">
                                <Newspaper className="h-4 w-4 text-green-500" />
                                <span>Haber (Widget içi)</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="modal">
                              <div className="flex items-center gap-2">
                                <Maximize2 className="h-4 w-4 text-orange-500" />
                                <span>Modal (Popup)</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">İçerik</Label>
                      <RichTextEditor
                        content={formData.content || ''}
                        onChange={handleContentChange}
                        placeholder="Banner içeriğini buraya yazın..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Buton Metni (Opsiyonel)</Label>
                        <Input
                          name="buttonText"
                          value={formData.buttonText || ''}
                          onChange={handleChange}
                          placeholder="Örn: Keşfet"
                          className="h-11 border-slate-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Buton Linki</Label>
                        <Input
                          name="buttonLink"
                          value={formData.buttonLink || ''}
                          onChange={handleChange}
                          placeholder="https://..."
                          className="h-11 border-slate-200"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Görünüm Ayarları */}
                <Card className="overflow-hidden border border-slate-200 shadow-sm bg-white rounded-xl">
                  <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                    <CardTitle className="text-base font-semibold text-slate-800">Görünüm Ayarları</CardTitle>
                    <CardDescription>Banner renklerini özelleştirin</CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <Label className="text-sm font-medium text-slate-700">Arkaplan Rengi</Label>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-12 h-12 rounded-lg border-2 border-slate-200 shadow-sm flex-shrink-0"
                            style={{ backgroundColor: formData.backgroundColor }}
                          />
                          <Input
                            type="color"
                            value={formData.backgroundColor}
                            onChange={(e) => handleColorChange(e.target.value, "backgroundColor")}
                            className="w-14 h-11 p-1 cursor-pointer border-slate-200"
                          />
                          <Input
                            value={formData.backgroundColor}
                            onChange={(e) => handleColorChange(e.target.value, "backgroundColor")}
                            className="font-mono flex-1 h-11 border-slate-200"
                            placeholder="#FFE600"
                          />
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label className="text-sm font-medium text-slate-700">Yazı Rengi</Label>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-12 h-12 rounded-lg border-2 border-slate-200 shadow-sm flex-shrink-0"
                            style={{ backgroundColor: formData.textColor }}
                          />
                          <Input
                            type="color"
                            value={formData.textColor}
                            onChange={(e) => handleColorChange(e.target.value, "textColor")}
                            className="w-14 h-11 p-1 cursor-pointer border-slate-200"
                          />
                          <Input
                            value={formData.textColor}
                            onChange={(e) => handleColorChange(e.target.value, "textColor")}
                            className="font-mono flex-1 h-11 border-slate-200"
                            placeholder="#000000"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Zamanlama */}
                <Card className="overflow-hidden border border-slate-200 shadow-sm bg-white rounded-xl">
                  <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                    <CardTitle className="text-base font-semibold text-slate-800">Zamanlama</CardTitle>
                    <CardDescription>Banner'ın ne zaman gösterileceğini ayarlayın</CardDescription>
                  </CardHeader>
                  <CardContent className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Başlangıç Tarihi</Label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            name="startDate"
                            type="datetime-local"
                            value={formatDateForInput(formData.startDate)}
                            onChange={handleDateChange}
                            className="pl-10 h-11 border-slate-200"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Bitiş Tarihi</Label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            name="endDate"
                            type="datetime-local"
                            value={formatDateForInput(formData.endDate)}
                            onChange={handleDateChange}
                            className="pl-10 h-11 border-slate-200"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 pt-2">
                      <Checkbox
                        id="active"
                        checked={formData.active}
                        onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                      />
                      <Label htmlFor="active" className="text-sm font-normal text-slate-600">
                        Banner aktif (gösterilecek)
                      </Label>
                    </div>
                  </CardContent>
                </Card>

                {/* Hedefleme */}
                <Card className="overflow-hidden border border-slate-200 shadow-sm bg-white rounded-xl">
                  <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                    <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Şirket Hedefleme
                    </CardTitle>
                    <CardDescription>Hiçbir şirket seçilmezse banner tüm şirketlere gösterilir</CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    <ScrollArea className="h-[200px] pr-4">
                      <div className="grid grid-cols-2 gap-3">
                        {companies.map((company) => (
                          <div
                            key={company._id}
                            className={`flex items-center space-x-2 p-3 rounded-lg border transition-colors cursor-pointer ${
                              (formData.targetCompanies || []).includes(company._id)
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}
                            onClick={() => handleCompanyToggle(company._id)}
                          >
                            <Checkbox
                              id={`company-${company._id}`}
                              checked={(formData.targetCompanies || []).includes(company._id)}
                              onChange={() => handleCompanyToggle(company._id)}
                            />
                            <Label htmlFor={`company-${company._id}`} className="font-normal cursor-pointer flex-1 text-sm">
                              {company.name}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>

                    {(formData.targetCompanies || []).length > 0 && (
                      <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <p className="text-sm text-purple-700">
                          <strong>{(formData.targetCompanies || []).length}</strong> şirket seçildi
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Sağ Panel - Canlı Önizleme */}
              <div className="space-y-4">
                <Card className="sticky top-6 overflow-hidden border border-slate-200 shadow-sm bg-white rounded-xl">
                  <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                    <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Canlı Önizleme
                      <span className="ml-auto text-xs font-normal text-slate-500 flex items-center gap-1">
                        {formData.displayType === 'banner' && <><Monitor className="h-3 w-3" /> Banner</>}
                        {formData.displayType === 'news' && <><Newspaper className="h-3 w-3" /> Haber</>}
                        {formData.displayType === 'modal' && <><Maximize2 className="h-3 w-3" /> Modal</>}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    {/* Banner Önizleme */}
                    {formData.displayType === 'banner' && (
                      <div className="rounded-lg border border-slate-200 overflow-hidden">
                        <div
                          className="p-4"
                          style={{
                            backgroundColor: formData.backgroundColor || '#FFE600',
                            color: formData.textColor || '#000000'
                          }}
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              {formData.content && (
                                <div
                                  className="text-sm opacity-90 line-clamp-2 tiptap-content"
                                  dangerouslySetInnerHTML={{ __html: replacePreviewVariables(formData.content) }}
                                />
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {formData.buttonText && (
                                <button className="px-3 py-1.5 rounded-md text-xs font-medium bg-black text-white">
                                  {formData.buttonText}
                                </button>
                              )}
                              <button className="p-1 rounded hover:bg-black/10">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="p-4 bg-slate-50 space-y-2">
                          <div className="h-3 bg-slate-200 rounded w-3/4"></div>
                          <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                          <div className="h-3 bg-slate-200 rounded w-5/6"></div>
                          <div className="h-16 bg-slate-200 rounded mt-4"></div>
                        </div>
                      </div>
                    )}

                    {/* Haber Önizleme */}
                    {formData.displayType === 'news' && (
                      <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
                        <div className="p-3 bg-slate-100 border-b border-slate-200 flex items-center gap-2">
                          <Newspaper className="h-4 w-4 text-slate-500" />
                          <span className="text-sm font-medium text-slate-700">Haberler</span>
                        </div>
                        <div className="p-3">
                          <div
                            className="p-3 rounded-lg border-l-4 cursor-pointer hover:bg-slate-50 transition-colors"
                            style={{ borderLeftColor: formData.backgroundColor || '#FFE600' }}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: formData.backgroundColor || '#FFE600' }}
                              >
                                <Newspaper className="h-4 w-4" style={{ color: formData.textColor || '#000000' }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-sm text-slate-900 truncate">
                                  {formData.title || 'Haber Başlığı'}
                                </h4>
                                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                                  {formData.content?.replace(/<[^>]*>/g, '').substring(0, 80) || 'Haber içeriği...'}
                                </p>
                                <span className="text-xs text-slate-400 mt-1 block">Az önce</span>
                              </div>
                            </div>
                          </div>
                          {/* Dummy news items */}
                          <div className="mt-2 p-3 rounded-lg border-l-4 border-slate-200 opacity-50">
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-lg bg-slate-200 flex-shrink-0"></div>
                              <div className="flex-1 space-y-1">
                                <div className="h-3 bg-slate-200 rounded w-3/4"></div>
                                <div className="h-2 bg-slate-200 rounded w-1/2"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Modal Önizleme */}
                    {formData.displayType === 'modal' && (
                      <div className="rounded-lg border border-slate-200 overflow-hidden">
                        <div className="bg-black/50 p-6 flex items-center justify-center min-h-[300px]">
                          <div className="bg-white rounded-xl shadow-2xl w-full max-w-[280px] overflow-hidden transform scale-90">
                            <div
                              className="relative p-4 pb-3"
                              style={{
                                backgroundColor: formData.backgroundColor || '#FFE600',
                                color: formData.textColor || '#000000'
                              }}
                            >
                              <button className="absolute top-3 right-3 p-1 rounded-full hover:bg-black/10">
                                <X className="w-4 h-4" />
                              </button>
                              <h2 className="text-base font-bold pr-6">{formData.title || 'Modal Başlığı'}</h2>
                              <div className="flex items-center gap-1 mt-1 text-xs opacity-70">
                                <Calendar className="w-3 h-3" />
                                {new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                              </div>
                            </div>
                            <div className="p-4 max-h-[120px] overflow-hidden">
                              <div
                                className="text-xs text-slate-600 line-clamp-4 tiptap-content"
                                dangerouslySetInnerHTML={{ __html: replacePreviewVariables(formData.content || '<p>Modal içeriği...</p>') }}
                              />
                            </div>
                            <div className="p-4 pt-0 flex items-center gap-2">
                              {formData.buttonText && formData.buttonLink && (
                                <button
                                  className="flex-1 px-3 py-2 rounded-lg text-xs font-medium"
                                  style={{
                                    backgroundColor: formData.backgroundColor || '#FFE600',
                                    color: formData.textColor || '#000000'
                                  }}
                                >
                                  {formData.buttonText}
                                </button>
                              )}
                              <button className="flex-1 px-3 py-2 rounded-lg text-xs font-medium bg-gray-100 text-gray-700">
                                Kapat
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-slate-500 text-center mt-3">
                      {formData.displayType === 'banner' && 'Banner kullanıcılara bu şekilde görünecek'}
                      {formData.displayType === 'news' && 'Haber yardım widget\'ında bu şekilde görünecek'}
                      {formData.displayType === 'modal' && 'Modal popup olarak ekranda bu şekilde görünecek'}
                    </p>

                    {/* Kaydet Butonu */}
                    <Button
                      type="submit"
                      disabled={isSaving || saveMutation.isPending}
                      className="w-full h-12 text-base bg-blue-600 hover:bg-blue-700 mt-4"
                    >
                      {isSaving || saveMutation.isPending ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Kaydediliyor...
                        </>
                      ) : (
                        <>
                          <Save className="h-5 w-5 mr-2" />
                          {isNewBanner ? "Duyuru Oluştur" : "Değişiklikleri Kaydet"}
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
